"use client";

import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

import { HeaderNavigationBar } from "@/components/layout";
import { BottomNavigationBar } from "@/components/layout";
import { useDevMode, useIsPWA } from "@/contexts/dev-mode-context";

export interface EnvironmentFrameProps {
	children?: ReactNode;
	className?: string;
	showHeader?: boolean;
	showBottomNav?: boolean;
	cropFromFullPage?: boolean;
}

export function EnvironmentFrame({
	children,
	className,
	showHeader = true,
	showBottomNav = true,
	cropFromFullPage = true,
}: EnvironmentFrameProps) {
	const { isPhoneMockupMode, isLoaded } = useDevMode();
	const isPWA = useIsPWA();

	// In PWA mode or responsive mode, use full-screen responsive layout
	const useResponsiveLayout = isPWA || !isPhoneMockupMode;

	// Don't render until we've loaded the dev mode preference to prevent flash
	if (!isLoaded) {
		return null;
	}

	// Responsive layout for PWA/mobile
	if (useResponsiveLayout) {
		return (
			<div
				className="w-full min-h-screen min-w-0 overflow-x-clip flex justify-center"
				data-testid="shared-shell"
				style={{ backgroundColor: "var(--color-vp-bg-primary)" }}
			>
				<div
					className={cn(
						"relative w-full min-h-screen min-w-0 max-w-[393px] overflow-x-clip",
						"flex flex-col",
						className
					)}
					style={{ backgroundColor: "var(--color-vp-bg-primary)" }}
				>
					{showHeader ? (
						<div
							className="sticky top-0 z-50 w-full"
							style={{
								paddingTop: "env(safe-area-inset-top, 0px)",
								height: "calc(140px + env(safe-area-inset-top, 0px))",
							}}
						>
							<HeaderNavigationBar />
						</div>
					) : null}

					<div
						className={cn(
							"flex-1 min-w-0 overflow-auto overflow-x-hidden",
							showBottomNav && "pb-[calc(68px+env(safe-area-inset-bottom,29px))]"
						)}
						style={{ backgroundColor: "var(--color-vp-bg-surface)" }}
					>
						{cropFromFullPage ? (
							<div
								className="relative w-full max-w-full [&>*]:!max-w-full"
								data-testid="shared-shell-content"
								style={{ transform: "translateY(-140px)", transformOrigin: "top left" }}
							>
								{children}
							</div>
						) : (
							children
						)}
					</div>

					{showBottomNav ? (
						<div
							className="fixed left-0 right-0 bottom-0 z-50 w-full max-w-[393px] mx-auto overflow-x-clip"
							style={{
								height: "calc(68px + env(safe-area-inset-bottom, 29px))",
								paddingBottom: "env(safe-area-inset-bottom, 29px)",
							}}
						>
							<BottomNavigationBar />
						</div>
					) : null}
				</div>
			</div>
		);
	}

	// Phone mockup layout for desktop development
	return (
		<div
			className="w-full min-h-screen min-w-0 overflow-x-clip flex justify-center"
			data-testid="shared-shell"
			style={{ backgroundColor: "var(--color-vp-bg-primary)" }}
		>
			<div
				className={cn("relative mx-auto", "w-full max-w-[393px] min-w-0 h-[852px] overflow-x-clip", className)}
				style={{ backgroundColor: "var(--color-vp-bg-primary)" }}
			>
				{showHeader ? (
					<div className="absolute left-0 top-0 h-[140px] w-full max-w-[393px] overflow-x-clip">
						<HeaderNavigationBar />
					</div>
				) : null}

				<div
					className={cn(
						"absolute left-0 right-0 min-w-0 overflow-x-hidden",
						showHeader ? "top-[140px]" : "top-0",
						showBottomNav ? "h-[600px] overflow-hidden" : "bottom-0 overflow-visible"
					)}
					style={{ backgroundColor: showHeader ? "var(--color-vp-bg-surface)" : "#F9FAFB" }}
				>
					{cropFromFullPage ? (
							<div
								className="relative w-full max-w-full [&>*]:!max-w-full"
								data-testid="shared-shell-content"
							style={{ transform: "translateY(-140px)", transformOrigin: "top left" }}
						>
							{children}
						</div>
					) : (
						children
					)}
				</div>

				{showBottomNav ? (
					<div className="absolute left-0 right-0 bottom-[29px] h-[68px]">
						<BottomNavigationBar />
					</div>
				) : null}
			</div>
		</div>
	);
}
